import React, { useState, useMemo, useRef, useCallback } from 'react';
import {
  Box, Typography, Modal, Fade, Paper, IconButton, Chip, ToggleButtonGroup, ToggleButton,
  List, ListItemButton, ListItemText, Tooltip,
  useTheme, alpha, useMediaQuery, Select, MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CircleIcon from '@mui/icons-material/Circle';
import StarIcon from '@mui/icons-material/Star';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import ViewTimelineIcon from '@mui/icons-material/ViewTimeline';

// イベント タイプ の 色 分け
// 2026-06-10 Phase 9.5: 旧 emoji marker (⚔👤💡🌍💕📖) を 2 文字 glyph に 置換
const EVENT_COLORS = {
  battle:     { color: '#f44336', label: '戦闘',   glyph: 'Bt' },
  character:  { color: '#ff9800', label: 'キャラ', glyph: 'Ch' },
  revelation: { color: '#9c27b0', label: '真相',   glyph: 'Rv' },
  world:      { color: '#4caf50', label: '世界観', glyph: 'Wd' },
  romance:    { color: '#e91e63', label: '恋愛',   glyph: 'Rm' },
  default:    { color: '#2196f3', label: '出来事', glyph: 'Ev' },
};

// テキストからイベントタイプを推定
const detectEventType = (text) => {
  if (/戦|討|攻撃|戦闘|襲|倒|剣|刀|鬼|魔/.test(text)) return 'battle';
  if (/覚醒|真実|正体|秘密|明かさ|発覚|真相/.test(text)) return 'revelation';
  if (/世界|国|村|町|城|洞窟|海|山|森|島|宮/.test(text)) return 'world';
  if (/恋|愛|好き|告白|キス|想い/.test(text)) return 'romance';
  if (/出会|仲間|加入|別れ|成長|変化|覚悟|誓/.test(text)) return 'character';
  return 'default';
};

// エピソード番号パーサー
const parseEpNum = (ep) => {
  if (typeof ep === 'number') return ep;
  if (typeof ep === 'string') {
    const m = ep.match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 0;
  }
  return 0;
};

// 重要度による丸のサイズ
const getDotSize = (significance) => {
  if (significance === 'high' || significance === '高' || significance === '重要') return 12;
  if (significance === 'medium' || significance === '中') return 8;
  return 6;
};

const isImportantEvent = (significance) =>
  significance === 'high' || significance === '高' || significance === '重要';

// ────────────────────────────────────────────────────
// ビュー1: 縦タイムライン（チャプター区切り+色分け）
// ────────────────────────────────────────────────────
const VerticalTimelineView = ({ filteredTimeline, embedded, theme, selectedEp, scrollToEpisode, episodeRefs }) => (
  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
    {/* エピソードジャンプバー */}
    <Box sx={{
      display: 'flex', gap: 0.5, px: 1.5, py: 0.75,
      overflowX: 'auto', flexShrink: 0,
      borderBottom: `1px solid ${theme.palette.divider}`,
      '&::-webkit-scrollbar': { height: 4 },
      '&::-webkit-scrollbar-thumb': { bgcolor: alpha(theme.palette.warning.main, 0.2), borderRadius: 2 },
    }}>
      {filteredTimeline.map(g => (
        <Chip
          key={g.epNum}
          label={`${g.epNum}話`}
          size="small"
          onClick={() => scrollToEpisode(g.epNum)}
          sx={{
            height: 24, fontSize: '0.65rem', fontWeight: 600, flexShrink: 0,
            bgcolor: selectedEp === g.epNum ? alpha(theme.palette.warning.main, 0.25) : alpha(theme.palette.warning.main, 0.08),
            color: 'warning.main',
            cursor: 'pointer',
            '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.2) },
          }}
        />
      ))}
    </Box>
    <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 1.5, md: 3 }, py: 2 }}>
    {filteredTimeline.map((group, gi) => (
      <Box
        key={group.epNum}
        ref={(el) => { episodeRefs.current[group.epNum] = el; }}
        sx={{ mb: 3 }}
      >
        {/* エピソードヘッダー */}
        <Box sx={{
          position: 'sticky', top: 0, zIndex: 5,
          bgcolor: 'background.paper',
          py: 0.75, mb: 1,
          borderBottom: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: '50%',
              bgcolor: 'warning.main', color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.8rem', fontWeight: 700,
            }}>
              {group.epNum}
            </Box>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700 }}>
              第{group.epNum}話
            </Typography>
            <Chip
              label={`${group.events.length}イベント`}
              size="small"
              sx={{ height: 22, fontSize: '0.7rem', bgcolor: alpha(theme.palette.warning.main, 0.1) }}
            />
          </Box>
        </Box>

        {/* イベントリスト */}
        <Box sx={{ position: 'relative', pl: 3 }}>
          {/* 縦線 */}
          <Box sx={{
            position: 'absolute', left: 9, top: 0, bottom: 0, width: 2,
            bgcolor: alpha(theme.palette.warning.main, 0.15),
          }} />

          {group.events.map((event, ei) => {
            const evColor = EVENT_COLORS[event.type] || EVENT_COLORS.default;
            const dotSize = getDotSize(event.significance);
            const important = isImportantEvent(event.significance);

            return (
              <Box key={ei} sx={{ position: 'relative', mb: 1.5, ml: 1 }}>
                {/* ドット */}
                <Box sx={{
                  position: 'absolute',
                  left: -24 - dotSize / 2 + 1,
                  top: dotSize > 8 ? 2 : 5,
                  width: dotSize, height: dotSize, borderRadius: '50%',
                  bgcolor: evColor.color,
                  boxShadow: important ? `0 0 8px ${alpha(evColor.color, 0.5)}` : 'none',
                  border: important ? `2px solid ${alpha(evColor.color, 0.3)}` : 'none',
                }} />

                {/* イベントカード */}
                <Paper
                  elevation={important ? 2 : 0}
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    borderLeft: `3px solid ${evColor.color}`,
                    bgcolor: important ? alpha(evColor.color, 0.04) : 'transparent',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    {important && (
                      <StarIcon sx={{ fontSize: '0.85rem', color: evColor.color, mt: 0.1, flexShrink: 0 }} />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{
                        fontSize: important ? '0.8rem' : '0.75rem',
                        fontWeight: important ? 600 : 400,
                        lineHeight: 1.5,
                        color: 'text.primary',
                      }}>
                        {event.event}
                      </Typography>
                      {event.timeMarker && (
                        <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', mt: 0.25 }}>
                          {event.timeMarker}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={evColor.label}
                      size="small"
                      sx={{
                        height: 16, fontSize: '0.55rem', flexShrink: 0,
                        bgcolor: alpha(evColor.color, 0.1),
                        color: evColor.color,
                      }}
                    />
                  </Box>
                </Paper>
              </Box>
            );
          })}
        </Box>

        {/* エピソード間コネクタ */}
        {gi < filteredTimeline.length - 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <Box sx={{ width: 1, height: 24, bgcolor: alpha(theme.palette.divider, 0.5) }} />
          </Box>
        )}
      </Box>
    ))}
    </Box>
  </Box>
);

// ────────────────────────────────────────────────────
// ビュー2: 横スクロール年表（エピソード列+縦積み）
// ────────────────────────────────────────────────────
const HorizontalTimelineView = ({ filteredTimeline, embedded, theme, isMobile }) => {
  const containerRef = useRef(null);
  const colRefs = useRef({});
  const COL_WIDTH = isMobile ? 160 : 200;
  const COL_GAP = isMobile ? 2 : 4;

  // パン＆ズーム — ref直接DOM操作（state再レンダリング回避）
  const contentRef = useRef(null);
  const zoomRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const dragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const panStart = useRef({ x: 0, y: 0 });
  const [zoomDisplay, setZoomDisplay] = useState(100); // 表示用のみ

  const applyTransform = useCallback(() => {
    if (!contentRef.current) return;
    contentRef.current.style.transform =
      `translate(${panRef.current.x}px, ${panRef.current.y}px) scale(${zoomRef.current})`;
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.08 : 0.08;
    zoomRef.current = Math.min(3, Math.max(0.3, zoomRef.current + delta));
    applyTransform();
    setZoomDisplay(Math.round(zoomRef.current * 100));
  }, [applyTransform]);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    dragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };
    panStart.current = { ...panRef.current };
    e.currentTarget.style.cursor = 'grabbing';
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    panRef.current = {
      x: panStart.current.x + (e.clientX - dragStart.current.x),
      y: panStart.current.y + (e.clientY - dragStart.current.y),
    };
    applyTransform();
  }, [applyTransform]);

  const handleMouseUp = useCallback((e) => {
    dragging.current = false;
    if (e.currentTarget) e.currentTarget.style.cursor = 'grab';
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 1) {
      dragging.current = true;
      dragStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      panStart.current = { ...panRef.current };
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!dragging.current || e.touches.length !== 1) return;
    panRef.current = {
      x: panStart.current.x + (e.touches[0].clientX - dragStart.current.x),
      y: panStart.current.y + (e.touches[0].clientY - dragStart.current.y),
    };
    applyTransform();
  }, [applyTransform]);

  const handleTouchEnd = useCallback(() => { dragging.current = false; }, []);

  const adjustZoom = useCallback((delta) => {
    zoomRef.current = Math.min(3, Math.max(0.3, zoomRef.current + delta));
    applyTransform();
    setZoomDisplay(Math.round(zoomRef.current * 100));
  }, [applyTransform]);

  const resetView = useCallback(() => {
    zoomRef.current = 1;
    panRef.current = { x: 0, y: 0 };
    applyTransform();
    setZoomDisplay(100);
  }, [applyTransform]);

  // エピソード列にパンする
  const jumpToEp = useCallback((epNum) => {
    const el = colRefs.current[epNum];
    if (!el || !containerRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const colLeft = (Array.from(el.parentElement.children).indexOf(el)) * (COL_WIDTH + COL_GAP);
    panRef.current = { x: -colLeft * zoomRef.current + 40, y: panRef.current.y };
    applyTransform();
  }, [COL_WIDTH, COL_GAP, applyTransform]);

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* エピソードジャンプバー */}
      <Box sx={{
        display: 'flex', gap: 0.5, px: 1.5, py: 0.75,
        overflowX: 'auto',
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&::-webkit-scrollbar': { height: 4 },
        '&::-webkit-scrollbar-thumb': { bgcolor: alpha(theme.palette.warning.main, 0.2), borderRadius: 2 },
      }}>
        {filteredTimeline.map(g => (
          <Chip
            key={g.epNum}
            label={`${g.epNum}話`}
            size="small"
            onClick={() => jumpToEp(g.epNum)}
            sx={{
              height: 24, fontSize: '0.65rem', fontWeight: 600, flexShrink: 0,
              bgcolor: alpha(theme.palette.warning.main, 0.08),
              color: 'warning.main',
              cursor: 'pointer',
              '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.2) },
            }}
          />
        ))}
      </Box>

      {/* ズームコントロール */}
      <Box sx={{
        position: 'absolute', bottom: 16, right: 16, zIndex: 10,
        display: 'flex', flexDirection: 'column', gap: 0.5,
      }}>
        <IconButton size="small" onClick={() => adjustZoom(0.2)}
          sx={{ bgcolor: 'background.paper', boxShadow: 2, width: 32, height: 32, fontSize: '1rem' }}>+</IconButton>
        <IconButton size="small" onClick={() => adjustZoom(-0.2)}
          sx={{ bgcolor: 'background.paper', boxShadow: 2, width: 32, height: 32, fontSize: '1rem' }}>-</IconButton>
        <IconButton size="small" onClick={resetView}
          sx={{ bgcolor: 'background.paper', boxShadow: 2, width: 32, height: 32, fontSize: '0.6rem' }}>1:1</IconButton>
        <Typography sx={{ textAlign: 'center', fontSize: '0.55rem', color: 'text.disabled' }}>
          {zoomDisplay}%
        </Typography>
      </Box>

      {/* パン＆ズーム可能な年表 */}
      <Box
        ref={containerRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        sx={{
          flex: 1,
          overflow: 'hidden',
          cursor: 'grab',
          position: 'relative',
          userSelect: 'none',
          touchAction: 'none',
          backgroundImage: `
            linear-gradient(${alpha(theme.palette.divider, 0.15)} 1px, transparent 1px),
            linear-gradient(90deg, ${alpha(theme.palette.divider, 0.15)} 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      >
        <Box
          ref={contentRef}
          sx={{
            display: 'flex',
            gap: `${COL_GAP}px`,
            minWidth: filteredTimeline.length * (COL_WIDTH + COL_GAP),
            alignItems: 'stretch',
            transformOrigin: '0 0',
            p: 2,
          }}
        >
          {filteredTimeline.map((group, gi) => (
            <Box
              key={group.epNum}
              ref={(el) => { colRefs.current[group.epNum] = el; }}
              sx={{
                width: COL_WIDTH,
                minWidth: COL_WIDTH,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* エピソードヘッダー */}
              <Box sx={{
                textAlign: 'center',
                mb: 0.75,
                position: 'sticky', top: 0, zIndex: 3,
                bgcolor: 'background.paper',
                py: 0.5,
              }}>
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  px: 1.5, py: 0.4, borderRadius: 2,
                  bgcolor: 'warning.main', color: 'white',
                  fontSize: '0.7rem', fontWeight: 700,
                }}>
                  EP{group.epNum}
                </Box>
              </Box>

              {/* 縦線 + イベント縦積み */}
              <Box sx={{
                position: 'relative',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}>
                {/* 中央縦線 */}
                <Box sx={{
                  position: 'absolute',
                  left: '50%', top: 0, bottom: 0,
                  width: 2, ml: '-1px',
                  bgcolor: alpha(theme.palette.warning.main, 0.15),
                  zIndex: 0,
                }} />

                {group.events.map((event, ei) => {
                  const evColor = EVENT_COLORS[event.type] || EVENT_COLORS.default;
                  const important = isImportantEvent(event.significance);

                  return (
                    <Box key={ei} sx={{
                      position: 'relative', zIndex: 1,
                      width: '100%',
                      mb: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}>
                      {/* ドット */}
                      <Box sx={{
                        width: important ? 12 : 8,
                        height: important ? 12 : 8,
                        borderRadius: '50%',
                        bgcolor: evColor.color,
                        boxShadow: important ? `0 0 6px ${alpha(evColor.color, 0.5)}` : 'none',
                        border: important ? `2px solid ${alpha(evColor.color, 0.3)}` : 'none',
                        mb: 0.5,
                        flexShrink: 0,
                      }} />

                      {/* イベントカード */}
                      <Paper
                        elevation={important ? 2 : 0}
                        sx={{
                          width: '92%',
                          p: 0.75,
                          borderRadius: 1.5,
                          borderLeft: `3px solid ${evColor.color}`,
                          bgcolor: important
                            ? alpha(evColor.color, 0.05)
                            : alpha(theme.palette.background.default, 0.7),
                          transition: 'all 0.15s',
                          '&:hover': {
                            boxShadow: 2,
                            bgcolor: alpha(evColor.color, 0.08),
                          },
                        }}
                      >
                        <Typography sx={{
                          fontSize: important ? '0.7rem' : '0.65rem',
                          fontWeight: important ? 600 : 400,
                          lineHeight: 1.45,
                          color: 'text.primary',
                        }}>
                          {important && (
                            <StarIcon sx={{
                              fontSize: '0.65rem', color: evColor.color,
                              verticalAlign: 'text-top', mr: 0.25,
                            }} />
                          )}
                          {event.event}
                        </Typography>
                        {event.timeMarker && (
                          <Typography sx={{
                            fontSize: '0.55rem', color: 'text.disabled', mt: 0.25,
                          }}>
                            {event.timeMarker}
                          </Typography>
                        )}
                      </Paper>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

// ────────────────────────────────────────────────────
// メインコンポーネント
// ────────────────────────────────────────────────────
const AITimelineModal = ({ open, onClose, memoryData, embedded = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState(null);
  const [selectedEp, setSelectedEp] = useState(null);
  const [viewMode, setViewMode] = useState(isMobile ? 'vertical' : 'horizontal');
  const timelineRef = useRef(null);
  const episodeRefs = useRef({});

  // タイムラインデータ構築
  const groupedTimeline = useMemo(() => {
    if (!memoryData?.fullMemory) return [];

    const timeline = memoryData.fullMemory.timeline || [];
    const plotPoints = memoryData.fullMemory.plotPoints || [];
    const allEvents = [];

    timeline.forEach(t => {
      const epNum = parseEpNum(t.episode || t.chapter);
      const event = typeof t.event === 'string' ? t.event : '';
      const timeMarker = typeof t.timeMarker === 'string' ? t.timeMarker : '';
      if (event && epNum) allEvents.push({ epNum, event, timeMarker, type: detectEventType(event), significance: '' });
    });

    plotPoints.forEach(p => {
      const epNum = parseEpNum(p.episode || p.chapter);
      const event = typeof p.event === 'string' ? p.event : '';
      const significance = typeof p.significance === 'string' ? p.significance : '';
      if (event && epNum) allEvents.push({ epNum, event, significance, type: detectEventType(event), timeMarker: '' });
    });

    const grouped = {};
    allEvents.forEach(e => {
      if (!grouped[e.epNum]) grouped[e.epNum] = { epNum: e.epNum, events: [] };
      const key = e.event.slice(0, 40);
      if (!grouped[e.epNum].events.some(ex => ex.event.slice(0, 40) === key)) {
        grouped[e.epNum].events.push(e);
      }
    });

    return Object.values(grouped).sort((a, b) => a.epNum - b.epNum);
  }, [memoryData]);

  // 検索 + タイプフィルター
  const filteredTimeline = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return groupedTimeline
      .map(g => ({
        ...g,
        events: g.events.filter(e => {
          if (q && !e.event.toLowerCase().includes(q)) return false;
          if (typeFilter && e.type !== typeFilter) return false;
          return true;
        }),
      }))
      .filter(g => g.events.length > 0);
  }, [groupedTimeline, searchText, typeFilter]);

  const totalEvents = filteredTimeline.reduce((s, g) => s + g.events.length, 0);

  // エピソードへスクロール
  const scrollToEpisode = useCallback((epNum) => {
    setSelectedEp(epNum);
    const el = episodeRefs.current[epNum];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  if (!memoryData) return null;

  // ── 共通コンテンツ ──
  const timelineContent = (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      height: '100%',
      bgcolor: 'background.paper',
    }}>
      {/* ツールバー: 検索 + ビュー切替 */}
      <Box sx={{ px: embedded ? 1.5 : 2, py: 0.75, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* 検索バー */}
          <Box sx={{
            flex: 1,
            display: 'flex', alignItems: 'center', gap: 0.5,
            bgcolor: alpha(theme.palette.text.primary, 0.04),
            borderRadius: 2, px: 1.5, py: 0.5,
          }}>
            <SearchIcon sx={{ fontSize: embedded ? '0.85rem' : '1rem', color: 'text.secondary' }} />
            <input
              type="text"
              placeholder="イベントを検索..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                border: 'none', outline: 'none', background: 'transparent',
                fontSize: embedded ? '0.75rem' : '0.85rem', width: '100%', color: 'inherit',
                fontFamily: 'inherit',
              }}
            />
          </Box>

          {/* ビュー切替ボタン（PC版のみ。スマホでは横年表が使いにくいため非表示） */}
          {!isMobile && <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => { if (v) setViewMode(v); }}
              size="small"
              sx={{
                height: 32,
                '& .MuiToggleButton-root': {
                  px: 1, py: 0.25,
                  borderColor: alpha(theme.palette.warning.main, 0.3),
                  '&.Mui-selected': {
                    bgcolor: alpha(theme.palette.warning.main, 0.12),
                    color: 'warning.main',
                    '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.18) },
                  },
                },
              }}
            >
              <ToggleButton value="vertical">
                <Tooltip title="縦タイムライン" arrow>
                  <ViewStreamIcon sx={{ fontSize: '1rem' }} />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="horizontal">
                <Tooltip title="横スクロール年表" arrow>
                  <ViewTimelineIcon sx={{ fontSize: '1rem' }} />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>}
        </Box>

        {/* 凡例（クリックでフィルタ） */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
          {Object.entries(EVENT_COLORS).filter(([k]) => k !== 'default').map(([key, val]) => (
            <Chip
              key={key}
              icon={<CircleIcon sx={{ fontSize: '0.5rem !important', color: `${val.color} !important` }} />}
              label={val.label}
              size="small"
              onClick={() => setTypeFilter(typeFilter === key ? null : key)}
              sx={{
                height: embedded ? 18 : 20, fontSize: embedded ? '0.55rem' : '0.6rem',
                cursor: 'pointer',
                bgcolor: typeFilter === key ? alpha(val.color, 0.15) : 'transparent',
                borderColor: typeFilter === key ? val.color : undefined,
                border: `1px solid ${typeFilter === key ? val.color : alpha(theme.palette.divider, 0.5)}`,
                fontWeight: typeFilter === key ? 600 : 400,
                '&:hover': { bgcolor: alpha(val.color, 0.08) },
              }}
            />
          ))}
          {typeFilter && (
            <Chip label="全表示" size="small" variant="outlined"
              onClick={() => setTypeFilter(null)}
              sx={{ height: embedded ? 18 : 20, fontSize: embedded ? '0.55rem' : '0.6rem', cursor: 'pointer' }} />
          )}
          <Chip label={`${filteredTimeline.length}話 / ${totalEvents}件`} size="small"
            sx={{ height: embedded ? 18 : 20, fontSize: embedded ? '0.55rem' : '0.6rem', ml: 'auto' }} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* サイドバー（縦ビュー + デスクトップ + 非embedded時のみ） */}
        {viewMode === 'vertical' && !isMobile && !embedded && (
          <Box sx={{
            width: 220, overflowY: 'auto', borderRight: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.text.primary, 0.02),
          }}>
            <List dense disablePadding>
              {filteredTimeline.map((group) => (
                <ListItemButton
                  key={group.epNum}
                  selected={selectedEp === group.epNum}
                  onClick={() => scrollToEpisode(group.epNum)}
                  sx={{ py: 0.5, px: 1.5 }}
                >
                  <Box sx={{
                    width: 22, height: 22, borderRadius: '50%', mr: 1, flexShrink: 0,
                    bgcolor: selectedEp === group.epNum
                      ? 'warning.main'
                      : alpha(theme.palette.warning.main, 0.15),
                    color: selectedEp === group.epNum ? 'white' : 'warning.main',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.6rem', fontWeight: 700,
                  }}>
                    {group.epNum}
                  </Box>
                  <ListItemText
                    primary={`第${group.epNum}話`}
                    primaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                  <Chip
                    label={group.events.length}
                    size="small"
                    sx={{ height: 18, fontSize: '0.6rem', minWidth: 24 }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        )}

        {/* モバイル: エピソードセレクタ（縦ビューのみ） */}
        {viewMode === 'vertical' && isMobile && !embedded && filteredTimeline.length > 0 && (
          <Box sx={{ position: 'absolute', bottom: 16, right: 16, zIndex: 10 }}>
            <Select
              value={selectedEp || ''}
              onChange={(e) => scrollToEpisode(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                bgcolor: 'background.paper', borderRadius: 2, boxShadow: 4,
                fontSize: '0.8rem', minWidth: 100,
              }}
            >
              <MenuItem value="" disabled>話数へ移動</MenuItem>
              {filteredTimeline.map(g => (
                <MenuItem key={g.epNum} value={g.epNum}>第{g.epNum}話 ({g.events.length}件)</MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {/* ビュー本体 */}
        {viewMode === 'vertical' ? (
          <VerticalTimelineView
            filteredTimeline={filteredTimeline}
            embedded={embedded}
            theme={theme}
            selectedEp={selectedEp}
            scrollToEpisode={scrollToEpisode}
            episodeRefs={episodeRefs}
          />
        ) : (
          <HorizontalTimelineView
            filteredTimeline={filteredTimeline}
            embedded={embedded}
            theme={theme}
            isMobile={isMobile}
          />
        )}
      </Box>

      {/* フッター */}
      <Box sx={{
        textAlign: 'center', py: embedded ? 1 : 1.5,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}>
        <SmartToyIcon sx={{ fontSize: embedded ? '1rem' : '1.2rem', color: 'text.disabled' }} />
        <Typography sx={{ fontSize: embedded ? '0.6rem' : '0.7rem', color: 'text.disabled', mt: 0.25 }}>
          全{memoryData?.totalEpisodesRead || 0}話分の時系列データ
        </Typography>
      </Box>
    </Box>
  );

  // embedded: パネル内にそのまま表示
  if (embedded) return timelineContent;

  // 通常: Modalでラップ
  return (
    <Modal open={open} onClose={onClose} closeAfterTransition aria-label="AIストーリー時系列">
      <Fade in={open}>
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
          {/* ヘッダー */}
          <Box sx={{
            p: 1.5, display: 'flex', alignItems: 'center', gap: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.warning.main, 0.03),
          }}>
            <SmartToyIcon sx={{ color: 'warning.main' }} />
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, flex: 1 }}>
              AIストーリー時系列
            </Typography>
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Box>
          {timelineContent}
        </Box>
      </Fade>
    </Modal>
  );
};

export { EVENT_COLORS, detectEventType };
export default AITimelineModal;
